/**
 * V2 Profile — the explorer's "look at me" page (2026-07-13 splash
 * treatment): a banner (their own background image, or a collage built
 * from their journal's photos), the identity panel overlapping it with
 * a big avatar, stat stamps, and the TROPHY CASE — V1's medal row
 * (user-panel.js:578-627), restored as the hero's brag strip. Follow
 * lives here and only here (familiarity rules).
 */
import { yoinkCard, thumbUrl, heroPhotos } from '../../ui/card.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { isFollowing, follow, unfollow } from '../follows/follows.js';
import { shareButton, handleShareClick, setUrlParam, getUrlParam, shareTextFrom } from '../../ui/share.js';
import { shareMenu } from '../../ui/share-menu.js';
import { challengeText } from '../category/leaderboard.js';
import { sharePhotosButton, createSharePhotosHandler } from '../../ui/share-photos.js';
import { hashtags } from '../../ui/share-card.js';
import { icon, categoryIcon } from '../../ui/icons.js';
import { mountMap, mapsAvailable, rowsInArea } from '../../ui/map-view.js';
import { embedButton, handleEmbedClick, isEmbed } from '../../ui/embed.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import * as gateway from '../../core/gateway.js';
import { fetchPublicLists } from '../lists/lists.js';
import { fetchPersonalCategoriesByOwner } from '../journal/journal.js';
import {
  fetchMilestones, fetchEarnedAwardRows, deriveAwards, earnedCount,
  journalStats, syncAwards,
} from './awards.js';
import { parseRegionParam, filterYoinksByRegion, regionLabel } from './region.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';
import { fetchBalance, bravoJournalLineHtml } from '../bravo/bravo.js';


// Split 2026-07-25: profile.js had grown to 856 lines against the plan's ~400
// budget. Data access moved to ./profile-data.js and the pure derivations to
// ./profile-derive.js. Everything this module exported before is re-exported
// below UNCHANGED — showcase.js, awards.js, geography.js and four test files
// import these names from here, and the point of the split was file size, not
// a new public interface.
import {
  fetchProfile, fetchAllUserYoinks, fetchTrophies, fetchNearTrophies,
  fetchTrophyGaps, fetchMedalTable,
  fetchStartedSets, fetchCollectionStory, saveCollectionStory,
} from './profile-data.js';
import {
  cssUrl, deriveNearTrophies, deriveTrophyGaps, deriveMedalTable, medalTally,
  deriveCategoryStacks, TROPHY_MEDALS,
  trophyMedal, versionedStorageUrl, avatarHtml, socialLinks,
} from './profile-derive.js';

export {
  fetchProfile, fetchAllUserYoinks, fetchTrophies, fetchNearTrophies,
  fetchTrophyGaps, fetchMedalTable,
  fetchStartedSets,
  deriveNearTrophies, deriveTrophyGaps, deriveMedalTable, medalTally,
  deriveCategoryStacks, TROPHY_MEDALS, trophyMedal,
  socialLinks,
};

const VISIBLE_STEP = 48; // client-side render slice for the card grids
const STACK_FILTER_MIN = 9; // stacks landing gets a find box past ~3 rows
// catFilter sentinel for the owner's uncategorized leftovers (no site
// category AND no personal one — legacy rows) — checked before category
// ids everywhere, so a category can never collide
const UNFILED_STACK = 'unfiled';
// A shelf's rows in this journal (shelf tiles + their counts)
const shelfRows = (rows, shelfId) => (rows || []).filter((y) => y.personal_category_id === shelfId);
const unfiledRows = (rows) => (rows || []).filter((y) => !y.category_id && !y.categories?.id && !y.personal_category_id);

/**
 * Mount the profile view.
 * @param {HTMLElement} el
 * @param {object} ctx - { store, router, params: { username } }
 */
export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let profile = null;
  let allYoinks = null; // null = still loading the journal
  let trophies = [];
  let catFilter = null; // null = stacks landing · 'all' · a category id
  let visibleCount = VISIBLE_STEP;
  let destroyed = false;
  let followBusy = false;
  let followError = '';
  let startedSets = []; // V1 Sets with progress (any profile)
  let publicLists = []; // M8 lists: public ones show on any profile
  // The explorer's personal categories (§11): shelves for their personal
  // yoinks, shown as tiles in the stacks grid to EVERY viewer. The
  // yoinks themselves ride allYoinks (they're regular rows); the shelves
  // are a flat read for the names.
  let myShelves = [];
  let milestones = []; // get_user_milestones rows (awards; RPC optional)
  let earnedRows = []; // user_awards rows (permanent tier floor; table optional)
  const embedded = isEmbed(); // declared before viewMode, which reads it
  // Shared links keep the view (?view=); otherwise the session's grid/map
  // choice follows you between pages, V1's global discoveryViewMode
  // (state.js:354, user-panel.js:755)
  let viewMode = getUrlParam('view') === 'map' ? 'map'
    : getUrlParam('view') === 'grid' ? 'grid'
    // An embed frame is a MAP by default whatever the session preferred:
    // the button that hands out the snippet says "Embed map", and the
    // reader is on someone else's site, where our session choice is noise
    : (embedded || store.get().viewMode === 'map') ? 'map' : 'grid';
  let mapHandle = null;
  let mapArea = null; // last map viewport — filters the cards on toggle-back (user report 2026-07-12)
  let embedShellBuilt = false; // ?embed=1 builds its map once — see renderEmbed
  let mapFitDone = false; // fit-to-journal only on FIRST map open; toggling back keeps your zoom (V1 user-panel.js:1687-1692)
  let stackTerm = ''; // instant local filter over the stacks landing
  // ?region= deep link from the geography page's drill-down — V1's
  // zoom-to-region filter (un-deferred 2026-07-16). Narrows the whole
  // yoinks section: stacks, grid, and map.
  let regionFilter = parseRegionParam(getUrlParam('region'));
  let regionRows = null; // memoized subset — identity checks depend on it

  // Collection stories (2026-07-26, landing-pages push step 2): the owner's
  // "what makes this one special" text on a filtered journal. The name is
  // historical and now crosses the vocabulary line the 2026-07-26 copy pass
  // drew — these hang off a CATEGORY, while "Collections" in the UI means
  // the curated Sets. The user-facing words say category; the internals
  // (storyByCat aside, the `collection-story` classes and the
  // user_collection_stories table) keep the old name because the table is
  // already applied by hand and cannot be renamed from here.
  // categoryId → undefined (not asked) | null (loading) | '' (none) | text.
  // A failed fetch lands as '' — until the migration runs the table 404s
  // and the story quietly stays away.
  const storyByCat = {};
  let storyEditing = false;
  let storySaving = false;
  let storyError = '';
  let storyDraft = null; // typed-but-unsaved text — survives async re-renders
  // Your own bank of bravos, on your own journal only. Read separately from
  // the profile so an un-applied migration hides one line instead of the page.
  let bravoBalance = null;

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  function followButton() {
    const me = store.get().user;
    if (me && profile && me.id === profile.id) {
      return `<button type="button" class="v2-btn-follow is-following" data-edit-profile>Edit profile</button>
        <button type="button" class="v2-btn-follow" data-import-places>Import your places</button>`;
    }
    if (!me || !profile) return '';
    const following = isFollowing(store, profile.id);
    return `
      <button type="button" class="v2-btn-follow ${following ? 'is-following' : ''}"
        data-follow-action aria-pressed="${following}" ${followBusy ? 'disabled' : ''}>
        ${followBusy ? '…' : following ? 'Following' : 'Follow'}
      </button>
      ${followError ? `<div class="v2-form-error" role="alert">${escapeHtml(followError)}</div>` : ''}`;
  }

  function render() {
    if (!profile) {
      el.innerHTML = `
        <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
        <div class="v2-empty">No explorer by that name.</div>`;
      return;
    }
    const name = profile.full_name || profile.username;
    // The visitor ribbon's line, sharpened with whose page this is — and,
    // filtered to one category, with which ("Like Dru's Airports? …")
    const ribbonStack = catFilter && catFilter !== 'all' && allYoinks
      && deriveCategoryStacks(allYoinks).find((c) => c.id === catFilter);
    setRibbonContext(ribbonStack
      ? `Like ${name}’s ${ribbonStack.name}? Start your own.`
      : `${name} collects the places they visit — photos, pins and collections. Start yours.`);
    const banner = profile.background_image
      ? versionedStorageUrl(profile.background_image, profile.updated_at)
      : null;
    const collage = banner ? '' : heroCollageHtml();
    if (embedded) { renderEmbed(name); return; }
    mapHandle?.cleanup();
    mapHandle = null;
    // Drilled in (a stamp or a category tapped) the page is ABOUT that
    // slice, not about the explorer — the 2026-07-27 tester report: "when I
    // click on Trail it once again introduces me with my stats, instead of
    // just focusing on the collection at hand". The cover — banner, avatar,
    // bio, stamp row — gives way to the category's own masthead, and the
    // only thing left pointing at the explorer is the way back.
    el.innerHTML = `
      ${focused() ? focusNavHtml(name) : `
      <nav class="v2-back" aria-label="Back">
        <a href="${basePath}/" data-route="discover">← Discover</a>
        <span class="yoink-share-row">
          ${shareMenu(
            sharePhotosButton(),
            journalHasPins() ? embedButton(`${name}’s journal`) : '',
            shareButton(`${name} on Yoink Adventures`)
          )}
        </span>
      </nav>`}
      ${focused() ? focusHeroHtml(name) : `
      <div class="cat-hero profile-hero">
        ${banner ? `<div class="profile-banner" style="background-image:url('${cssUrl(banner)}')"></div>` : collage}
        <div class="cat-hero-panel${banner || collage ? ' has-collage' : ''}">
          <header class="v2-profile-head">
            ${avatarHtml(profile)}
            <div class="profile-names">
              <h2 class="profile-name">${escapeHtml(name)}</h2>
              <div class="profile-username">@${escapeHtml(profile.username)}</div>
              ${profile.location ? `<div class="profile-location">${escapeHtml(profile.location)}</div>` : ''}
              ${socialLinks(profile)}
            </div>
            ${followButton()}
          </header>
          ${profile.bio ? `<p class="profile-bio">${escapeHtml(profile.bio)}</p>` : ''}
          ${statsStamps()}
          ${store.get().user?.id === profile.id ? bravoJournalLineHtml(bravoBalance) : ''}
        </div>
      </div>`}
      <section aria-label="Yoinks by ${escapeHtml(name)}" data-journal-section>
        ${catFilter === UNFILED_STACK ? '' : `
        <div class="v2-toggles">
          <div class="v2-scope" role="group" aria-label="View">
            <button class="v2-scope-btn ${viewMode === 'grid' ? 'scope-active' : ''}"
              data-profile-view="grid" aria-pressed="${viewMode === 'grid'}">Grid</button>
            <button class="v2-scope-btn ${viewMode === 'map' ? 'scope-active' : ''}"
              data-profile-view="map" aria-pressed="${viewMode === 'map'}">Map</button>
          </div>
        </div>`}
        ${regionFilter ? `
        <p class="log-hint feed-area-line">Showing yoinks in <strong>${escapeHtml(regionLabel(regionFilter))}</strong> ·
          <button type="button" class="v2-search-clear" data-clear-region>Show everywhere</button>
        </p>` : ''}
        ${viewMode === 'map' && catFilter !== UNFILED_STACK ? '<div class="v2-map" data-profile-map></div>' : yoinksBody()}
      </section>`;
    if (viewMode === 'map' && catFilter !== UNFILED_STACK) {
      // The whole journal on the map, framed to it (V1 behavior) — not
      // just one page of it (M8.5 Tier 1). Framed on the FIRST open only:
      // toggling grid↔map keeps the zoom you left it at (V1's
      // mapInitializedOnce), so a Florida zoom survives the round trip.
      mapHandle = mountMap(el.querySelector('[data-profile-map]'), {
        store, router, fitToMarkers: !mapFitDone,
        onBoundsChange: (b) => { mapArea = b; },
      });
      if (mapsAvailable()) {
        mapHandle.setYoinks(filteredYoinks());
        // Not "done" until there were markers to frame — a ?view=map deep
        // link mounts the map before the journal rows arrive
        if (allYoinks?.length) mapFitDone = true;
      }
    }
  }

  /**
   * Drilled into one slice of the journal? A category, the flat all-yoinks
   * grid, and the Locations stamp's map all set catFilter, so that alone
   * says the page has moved past the cover.
   */
  function focused() {
    return catFilter !== null;
  }

  /**
   * Park the reader at the top of the page's own content after a drill-in
   * or a step back. Every one of those repaints #v2-app under a scroll
   * position that belonged to the PREVIOUS screen — you tap a category
   * card sitting two thirds down the stacks grid and the collection you
   * asked for opens already scrolled past its own masthead (2026-07-28
   * report: "the page loads in the middle, scrolled down").
   *
   * The target is the app container, not window 0: its top edge is the way
   * back, and the collage sits immediately under it — "just above the
   * header image collage", the same ask. The site header above it is
   * chrome the reader scrolled past on the way in, and dragging them back
   * up through it would only bury the collection again.
   *
   * The reachability check is not defensive padding. A collection short
   * enough that its top cannot climb to the top of the screen fits on the
   * screen whole, and asking anyway leaves the browser clamped partway
   * down — measured at the moment of the repaint, before the hero's story
   * block has arrived from its fetch, so the document is at its shortest
   * exactly when this runs. Such a page starts at 0 instead: everything is
   * visible either way, and offset 0 is also where scroll anchoring stops
   * nudging the page as those late pieces land.
   *
   * Instant, never smooth: the repaint already swapped every pixel, so
   * there is no continuity for an animation to preserve.
   */
  function scrollToPageTop() {
    const top = el.getBoundingClientRect().top + window.scrollY;
    const reach = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo(0, top <= reach ? top : 0);
  }

  /** The category this focus is on — null on the flat all-yoinks view. */
  function focusStack() {
    if (!allYoinks || !catFilter || catFilter === 'all') return null;
    return deriveCategoryStacks(allYoinks, trophies).find((c) => c.id === catFilter) || null;
  }

  /**
   * The frame on someone else's page: an identity strip and the map,
   * nothing else — the same shape and the same CSS the category page's
   * embed uses (features/category buildShell). A banner, a collage and a
   * stamp row inside a 480px frame only force scrolling in a box.
   *
   * Built ONCE. render() runs again on auth settling and store noise,
   * and a remount would construct a second (billed) Google map on a
   * stranger's page — so later passes only re-pin the map that is
   * already there.
   */
  function renderEmbed(name) {
    const rows = filteredYoinks();
    const stack = focusStack();
    const title = catFilter === UNFILED_STACK ? `${name} · Uncategorized`
      : stack ? `${name} · ${stack.name}` : `${name}’s journal`;
    if (!embedShellBuilt) {
      embedShellBuilt = true;
      el.innerHTML = `
        <div class="cat-embed-head">
          <span class="cat-head-icon" aria-hidden="true" data-embed-icon></span>
          <strong data-embed-name></strong>
          <span class="cat-embed-count" data-embed-count aria-live="polite"></span>
        </div>
        <section class="cat-map-splash cat-embed-splash" aria-label="Map" data-embed-region>
          <div class="v2-map cat-splash-map" data-profile-map></div>
        </section>`;
      mapHandle = mountMap(el.querySelector('[data-profile-map]'), {
        store, router, fitToMarkers: true,
      });
    }
    // The strip is written on EVERY pass: the shell is built before the
    // journal rows land, so on the first one there is no category to name
    // yet — only the map itself is built once.
    const nameEl = el.querySelector('[data-embed-name]');
    if (nameEl) nameEl.textContent = title;
    const iconEl = el.querySelector('[data-embed-icon]');
    if (iconEl) iconEl.innerHTML = stack ? categoryIcon(stack.category, 22) : icon('grid', 22);
    const count = el.querySelector('[data-embed-count]');
    if (count) count.textContent = rows.length ? `${rows.length} yoink${rows.length === 1 ? '' : 's'}` : '';
    // Named for a screen reader too — "Map" alone says nothing about whose
    el.querySelector('[data-embed-region]')?.setAttribute('aria-label', `${title} map`);
    if (mapsAvailable()) mapHandle?.setYoinks(rows);
  }

  /**
   * The way back to the cover. It replaces "← Discover" rather than
   * joining it: two back doors on one screen is the noise the focus was
   * meant to remove, and the journal is the more useful parent.
   *
   * The share actions go through shareMenu like every other surface —
   * this row was missed when the pulldown landed (2026-07-27), so a
   * drilled-in collection still wore two outlined buttons that wrapped to
   * their own line on a phone and shouted over the collage below them.
   */
  function focusNavHtml(name) {
    const own = store.get().user?.id === profile.id;
    const stack = focusStack();
    return `
      <nav class="v2-back" aria-label="Back">
        <button type="button" class="v2-back-link" data-stacks-back>←
          ${own ? 'My journal' : `${escapeHtml(name)}’s journal`}</button>
        ${catFilter === UNFILED_STACK ? '' : `<span class="yoink-share-row">
          ${shareMenu(
            sharePhotosButton(),
            journalHasPins(filteredYoinks())
              ? embedButton(stack ? `${name}’s ${stack.name}` : `${name}’s journal`,
                { category: filterSlug() })
              : '',
            shareButton(stack ? `${name}’s ${stack.name} on Yoink Adventures` : `${name} on Yoink Adventures`)
          )}
        </span>`}
      </nav>`;
  }

  /**
   * The focused masthead — the category page's shape, scoped to one
   * explorer: the category's own name and photos out front, with the
   * owner and their count to the RIGHT of it ("Trail · Dru Morgan · 131
   * yoinks", the tester's exact ask) rather than a second introduction.
   */
  function focusHeroHtml(name) {
    const own = store.get().user?.id === profile.id;
    const unfiledFocus = catFilter === UNFILED_STACK;
    const stack = focusStack();
    const rows = filteredYoinks();
    const collage = focusCollageHtml(rows);
    const title = unfiledFocus ? 'Uncategorized' : stack ? stack.name : 'All yoinks';
    const places = new Set(rows.map((y) => y.location_id).filter(Boolean)).size;
    return `
      <header class="cat-hero journal-focus-hero">
        ${collage}
        <div class="cat-hero-panel${collage ? ' has-collage' : ''}">
          <div class="journal-cat-head">
            <h2 class="journal-cat-title">
              ${unfiledFocus ? '<span aria-hidden="true">🗂️</span>' : stack ? categoryIcon(stack.category, 30) : icon('grid', 28)}
              <span>${escapeHtml(title)}</span>
            </h2>
            <button type="button" class="journal-cat-owner" data-stacks-back
              aria-label="Back to ${own ? 'your' : `${escapeHtml(name)}’s`} journal">
              ${avatarHtml(profile)}
              <span class="journal-cat-owner-name">${escapeHtml(name)}</span>
              <span class="journal-cat-owner-count">${rows.length} yoink${rows.length === 1 ? '' : 's'}${
                places && places !== rows.length ? ` · ${places} place${places === 1 ? '' : 's'}` : ''}</span>
            </button>
          </div>
          ${unfiledFocus ? '<p class="log-hint">Yoinks without a category yet — edit one to file it.</p>' : ''}
          ${stack ? `<div data-collection-story>${storyBlockHtml(stack, own)}</div>` : ''}
          ${own && stack && stack.places > 0 ? `
          <button type="button" class="v2-btn-more cat-challenge" data-profile-challenge>
            ${icon('share', 14)}<span data-share-label>Challenge a friend</span>
          </button>` : ''}
        </div>
      </header>`;
  }

  /** The focused hero's collage, built from the rows this focus shows. */
  function focusCollageHtml(rows) {
    const photos = heroPhotos(rows || []);
    if (photos.length < 3) return '';
    return `
      <div class="cat-hero-collage${photos.length >= 5 ? ' collage-mosaic' : ''}" aria-hidden="true">
        ${photos.slice(0, 5).map((url, i) => `<img src="${thumbUrl(url)}" alt="" loading="${i < 3 ? 'eager' : 'lazy'}"
          data-fallback-src="${url}">`).join('')}
      </div>`;
  }

  /**
   * The banner when no custom one is set: a mosaic from the explorer's
   * own newest photos — the same collage component as the category
   * splash, from rows the page loads anyway.
   */
  function heroCollageHtml() {
    const photos = heroPhotos(allYoinks || []);
    if (photos.length < 3) return '';
    return `
      <div class="cat-hero-collage${photos.length >= 5 ? ' collage-mosaic' : ''}" aria-hidden="true">
        ${photos.slice(0, 5).map((url, i) => `<img src="${thumbUrl(url)}" alt="" loading="${i < 3 ? 'eager' : 'lazy'}"
          data-fallback-src="${url}">`).join('')}
      </div>`;
  }

  /** Milestone-award families this explorer has earned (awards.js). */
  function earnedAwardsCount() {
    return earnedCount(deriveAwards({
      yoinkCount: allYoinks?.length || 0,
      sets: startedSets,
      milestones,
      stats: journalStats(allYoinks || []),
      earnedRows,
    }));
  }

  /** Distinct countries in the journal (locations.country rides on FEED_SELECT). */
  function countryCount() {
    if (!allYoinks) return 0;
    return new Set(allYoinks.map((y) => y.locations?.country).filter(Boolean)).size;
  }

  /**
   * The hero's index, as ONE row of stamps (2026-07-26). Until now the
   * hero carried V1's Activity summary (user-panel.js:854-865) AS WELL AS
   * the 2026-07-13 doors row, so Trophies and Awards each appeared twice —
   * once as a dead circle, once as a live button, same number. Merged: the
   * count you read is the thing you tap. Every stamp goes somewhere, and
   * each count comes from data the page is already loading.
   */
  function statsStamps() {
    const own = store.get().user?.id === profile.id;
    const stamps = [];
    /** @param {{count: (number|string), label: string, tone: string, aria: string, door?: string, jump?: string}} s */
    const add = (s) => {
      const target = s.door ? `data-door="${s.door}"` : `data-jump="${s.jump}"`;
      stamps.push(`<button type="button" class="pstamp ${s.tone}" ${target}
        aria-label="${escapeHtml(s.aria)}"><span class="big">${s.count}</span><span class="lbl">${s.label}</span></button>`);
    };

    const journal = allYoinks || [];
    const countries = countryCount();
    if (journal.length) {
      const places = new Set(journal.map((y) => y.location_id).filter(Boolean)).size;
      add({
        count: journal.length, label: `Yoink${journal.length === 1 ? '' : 's'}`, tone: 'ps-red',
        jump: 'all', aria: `Show all ${journal.length} yoink${journal.length === 1 ? '' : 's'}`,
      });
      add({
        count: places, label: `Location${places === 1 ? '' : 's'}`, tone: 'ps-blue',
        jump: 'map', aria: `Show ${places} location${places === 1 ? '' : 's'} on the map`,
      });
      if (countries) {
        add({
          count: countries, label: `Countr${countries === 1 ? 'y' : 'ies'}`, tone: 'ps-teal',
          door: 'user-geography', aria: `Geography — ${countries} countr${countries === 1 ? 'y' : 'ies'}`,
        });
      }
    }
    // Countries needs locations.country, which the geography page backfills
    // lazily — a journal that hasn't been through it yet (or has no yoinks
    // at all) would read "0 Countries". That case keeps the map glyph and
    // the old name, so the door is never missing.
    if (!countries) {
      add({
        count: `<span class="pstamp-glyph">${icon('map', 30)}</span>`, label: 'Geography',
        tone: 'ps-teal', door: 'user-geography', aria: 'Geography — where this journal has been',
      });
    }

    const medals = (trophies || []).filter((t) => TROPHY_MEDALS[t.rank]).length;
    if (medals) {
      add({
        count: medals, label: `Troph${medals === 1 ? 'y' : 'ies'}`, tone: 'ps-gold',
        door: 'user-trophies', aria: `Trophy case — ${medals} top-3 finish${medals === 1 ? '' : 'es'}`,
      });
    }
    const awards = earnedAwardsCount();
    if (awards) {
      add({
        count: awards, label: `Award${awards === 1 ? '' : 's'}`, tone: 'ps-green',
        door: 'user-awards', aria: `Awards — ${awards} milestone${awards === 1 ? '' : 's'} earned`,
      });
    }
    if (startedSets.length) {
      add({
        count: startedSets.length, label: `Collection${startedSets.length === 1 ? '' : 's'}`,
        tone: 'ps-plum', door: 'user-collections',
        aria: `Collections — ${startedSets.length} started`,
      });
    }
    // The journal's distinct YOINKED categories — public data (the stacks
    // landing below shows the same thing), so every profile gets the stamp
    // and the count matches that landing (2026-07-17 ask)
    const journalCats = allYoinks ? deriveCategoryStacks(allYoinks).length : 0;
    if (journalCats) {
      add({
        count: journalCats, label: `Categor${journalCats === 1 ? 'y' : 'ies'}`, tone: 'ps-rust',
        door: 'user-categories', aria: `Categories — ${journalCats} in this journal`,
      });
    }
    if (publicLists.length || own) {
      // Your own stamp goes to My lists (private ones live there);
      // visitors get the public-lists page
      add({
        count: publicLists.length, label: `List${publicLists.length === 1 ? '' : 's'}`,
        tone: 'ps-ink', door: own ? 'lists' : 'user-lists',
        aria: `Lists — ${publicLists.length} public`,
      });
    }
    if (!stamps.length) return '';
    return `<nav class="place-stamps profile-stamps" aria-label="This explorer’s journal, by the numbers">${stamps.join('')}</nav>`;
  }

  /**
   * The journal as the yoinks section sees it: everything, or the
   * ?region= subset. Memoized so callers can compare by identity. The
   * hero (stats stamps, collage, share card) stays on the full journal —
   * the region filter narrows the section, not the identity.
   */
  function journalRows() {
    if (!allYoinks || !regionFilter) return allYoinks;
    if (!regionRows) regionRows = filterYoinksByRegion(allYoinks, regionFilter);
    return regionRows;
  }

  /**
   * Is there anything to put on a map? An embed of a journal with no
   * located yoinks is an empty frame on someone else's site, so the
   * Embed button stays off until there is at least one pin. Defaults to
   * the whole journal; the focused row passes its own slice.
   */
  function journalHasPins(rows = allYoinks) {
    return !!rows?.some((y) => y.location_id);
  }

  function filteredYoinks() {
    if (catFilter === UNFILED_STACK) return unfiledRows(journalRows());
    const rows = journalRows();
    if (!rows) return [];
    if (!catFilter || catFilter === 'all') return rows;
    return rows.filter((y) => (y.category_id || y.categories?.id) === catFilter);
  }

  /**
   * What the stacks landing derives from: the journal (region-narrowed
   * when a filter is set), narrowed to the map viewport when one is set
   * and actually narrows. Returns journalRows() ITSELF when not
   * narrowing so callers can tell by identity.
   */
  function stacksSource() {
    const rows = journalRows();
    if (!rows || !mapArea) return rows;
    const areaRows = rowsInArea(rows, mapArea);
    return areaRows.length < rows.length ? areaRows : rows;
  }

  function stackCard(stack) {
    const medal = trophyMedal(stack.trophyRank);
    const hero = stack.heroUrl
      ? `<img class="stack-hero" src="${thumbUrl(stack.heroUrl)}" alt="" loading="lazy"
          data-fallback-src="${stack.heroUrl}">`
      : `<div class="stack-hero stack-hero-blank">${categoryIcon(stack.category, 40)}</div>`;
    return `
      <button type="button" class="journal-stack" data-stack-id="${stack.id}"
        aria-label="View ${escapeHtml(stack.name)} category">
        ${hero}
        <span class="stack-body">
          ${categoryIcon(stack.category, 18)}
          <span class="stack-name">${escapeHtml(stack.name)}</span>
          ${medal ? `<span class="stack-trophy" role="img" aria-label="Top-3 collector">${medal}</span>` : ''}
          <span class="stack-count">${stack.places} place${stack.places === 1 ? '' : 's'}</span>
        </span>
      </button>`;
  }

  /**
   * The personal tiles leading the stacks grid (§11): one tile per
   * personal category, for EVERY viewer — the yoinks are regular rows in
   * allYoinks, the shelves name them. Tapping one opens the shelf's own
   * page (/user/{u}/category/{slug}). The owner also gets an
   * "Uncategorized" tile for legacy rows with no shelf at all. Built
   * inside stacksGridHtml (the find-box patch rewrites that grid's
   * innerHTML), and the find box filters these by name like any other
   * stack.
   */
  function journalTilesHtml(needle = '') {
    const own = store.get().user?.id === profile.id;
    const rows = journalRows() || [];
    const tiles = [];
    for (const shelf of myShelves) {
      if (needle && !shelf.name.toLowerCase().includes(needle)) continue;
      const list = shelfRows(rows, shelf.id);
      // Visitors see tiles only for shelves with yoinks; the owner sees
      // every shelf (an empty one is theirs to fill or rename)
      if (!own && !list.length) continue;
      const heroImg = list.find((y) => Array.isArray(y.images) && y.images[0]);
      const heroUrl = heroImg
        ? (heroImg.images[0].startsWith('http') ? heroImg.images[0] : gateway.getPublicUrl('yoinks', heroImg.images[0]))
        : null;
      tiles.push(`
      <button type="button" class="journal-stack journal-stack-private" data-shelf-tile="${escapeHtml(shelf.slug)}"
        aria-label="View ${escapeHtml(shelf.name)} — personal category, ${list.length} yoink${list.length === 1 ? '' : 's'}">
        ${heroUrl
          ? `<img class="stack-hero" src="${thumbUrl(heroUrl)}" alt="" loading="lazy" data-fallback-src="${heroUrl}">`
          : `<div class="stack-hero stack-hero-blank">${shelf.emoji ? escapeHtml(shelf.emoji) : '🔒'}</div>`}
        <span class="stack-body">
          <span aria-hidden="true">🔒</span>
          <span class="stack-name">${escapeHtml(shelf.name)}</span>
          <span class="stack-count">${list.length} yoink${list.length === 1 ? '' : 's'}</span>
        </span>
      </button>`);
    }
    if (own) {
      const unfiled = unfiledRows(rows);
      if (unfiled.length && (!needle || 'uncategorized'.includes(needle))) {
        const heroImg = unfiled.find((y) => Array.isArray(y.images) && y.images[0]);
        const heroUrl = heroImg
          ? (heroImg.images[0].startsWith('http') ? heroImg.images[0] : gateway.getPublicUrl('yoinks', heroImg.images[0]))
          : null;
        const n = unfiled.length;
        tiles.push(`
      <button type="button" class="journal-stack journal-stack-private" data-stack-id="${UNFILED_STACK}"
        aria-label="View your uncategorized yoinks — ${n}">
        ${heroUrl
          ? `<img class="stack-hero" src="${thumbUrl(heroUrl)}" alt="" loading="lazy" data-fallback-src="${heroUrl}">`
          : '<div class="stack-hero stack-hero-blank">🗂️</div>'}
        <span class="stack-body">
          <span aria-hidden="true">🗂️</span>
          <span class="stack-name">Uncategorized</span>
          <span class="stack-count">${n} yoink${n === 1 ? '' : 's'}</span>
        </span>
      </button>`);
      }
    }
    return tiles.join('');
  }

  /** The UNCATEGORIZED drill-in: plain yoink cards (edit one to file it). */
  function journalBody() {
    const list = unfiledRows(journalRows());
    return `
      ${list.length
        ? `<div class="v2-grid">${list.slice(0, visibleCount).map(yoinkCard).join('')}</div>`
        : '<div class="v2-empty">Nothing uncategorized — every yoink has a home.</div>'}
      <div class="v2-feed-foot" aria-live="polite">
        ${list.length > visibleCount ? '<button type="button" class="v2-btn-more" data-show-more>Show more</button>' : ''}
      </div>`;
  }

  /**
   * Patch the story box in place — NEVER a full render() from here or
   * the editing handlers: a full repaint tears down and remounts the
   * map, which would throw away the visitor's zoom (and the viewport
   * filter riding on it) whenever the story fetch settled mid-look.
   * Same pattern as the stacks-grid filter patch below.
   */
  function patchStoryBox() {
    const box = el.querySelector('[data-collection-story]');
    if (!box || !allYoinks) return;
    const stack = catFilter && catFilter !== 'all'
      && deriveCategoryStacks(allYoinks).find((c) => c.id === catFilter);
    if (!stack) return;
    box.innerHTML = storyBlockHtml(stack, store.get().user?.id === profile.id);
  }

  /** Kick off the story fetch for a category, once. */
  function ensureStory(categoryId) {
    if (storyByCat[categoryId] !== undefined) return;
    storyByCat[categoryId] = null;
    fetchCollectionStory(profile.id, categoryId)
      .then((story) => { storyByCat[categoryId] = story; })
      .catch(() => { storyByCat[categoryId] = ''; }) // table not applied yet
      .then(() => { if (!destroyed) patchStoryBox(); });
  }

  /**
   * The category's story, between the filtered heading and its cards:
   * visitors read it, the owner writes it in place (buttons and a plain
   * textarea — iPad first, like the list-page note editor).
   */
  function storyBlockHtml(stack, own) {
    const story = storyByCat[stack.id];
    if (story === undefined || story === null) return ''; // not loaded yet
    if (own && storyEditing) {
      return `
        <div class="collection-story-edit">
          <textarea data-story-input rows="4" maxlength="1000"
            aria-label="The story behind your ${escapeHtml(stack.name)}"
            placeholder="What makes this one special to you?">${escapeHtml(storyDraft !== null ? storyDraft : story)}</textarea>
          ${storyError ? `<div class="v2-form-error" role="alert">${escapeHtml(storyError)}</div>` : ''}
          <div class="collection-story-actions">
            <button type="button" class="v2-btn-more" data-story-save ${storySaving ? 'disabled' : ''}>
              ${storySaving ? 'Saving…' : 'Save story'}</button>
            <button type="button" class="v2-btn-more" data-story-cancel ${storySaving ? 'disabled' : ''}>Cancel</button>
          </div>
        </div>`;
    }
    if (story) {
      return `
        <div class="collection-story">
          <p class="collection-story-text">${escapeHtml(story)}</p>
          ${own ? '<button type="button" class="v2-btn-more" data-story-edit>Edit story</button>' : ''}
        </div>`;
    }
    return own
      ? `<button type="button" class="v2-btn-more collection-story-add" data-story-edit>
          ✎ Tell the story behind your ${escapeHtml(stack.name)}</button>`
      : '';
  }

  /**
   * Grid mode body. The landing is V1's category stacks
   * (user-panel.js renderCategoryGrid); tapping a stack shows that
   * category's yoinks with a way back. 'all' is the flat everything view.
   */
  function yoinksBody() {
    const own = store.get().user?.id === profile.id;
    if (allYoinks === null) return '<div class="v2-loading">Unrolling the map…</div>';
    if (catFilter === UNFILED_STACK) return journalBody();
    if (!allYoinks.length) {
      // No yoinks at all — but the owner's empty shelves still need
      // their tiles, so they get their own grid here
      const journalTiles = journalTilesHtml();
      return `<div class="v2-empty">${own ? 'You haven’t' : 'This explorer hasn’t'} logged any yoinks yet.</div>
        ${journalTiles ? `<div class="journal-stacks">${journalTiles}</div>` : ''}`;
    }
    const journal = journalRows();
    if (!journal.length) {
      // The region filter matched nothing (stale link, backfilled data)
      return `<div class="v2-empty">No yoinks in ${escapeHtml(regionLabel(regionFilter))} yet.</div>`;
    }
    if (!catFilter) {
      // The stacks landing follows the map viewport too — V1 built its
      // category grid from the bounds-filtered yoinks
      // (user-panel.js renderCategoryGrid ← filteredYoinks)
      const source = stacksSource();
      const areaActive = source !== journal;
      const stacks = deriveCategoryStacks(source, trophies);
      // A prolific journal gets a find-as-you-type box over its stacks
      // (2026-07-12 feedback: too many categories to scan). Local data,
      // so per-keystroke like /categories — no debounce, no spinner.
      const filterBox = stacks.length >= STACK_FILTER_MIN ? `
        <div class="v2-search journal-stack-filter">
          <label class="visually-hidden" for="stack-filter">Find a category</label>
          <input id="stack-filter" type="search" placeholder="Find a category…"
            autocomplete="off" value="${escapeHtml(stackTerm)}" data-stack-filter>
        </div>` : '';
      return `
        ${filterBox}
        ${areaActive ? `
        <p class="log-hint feed-area-line">Showing ${source.length} of ${journal.length} in the map’s view ·
          <button type="button" class="v2-search-clear" data-clear-map-area>Show everywhere</button>
        </p>` : ''}
        <div class="journal-stacks" data-stacks-grid>
          ${stacksGridHtml(stacks)}
        </div>
        <div class="v2-feed-foot">
          <button type="button" class="v2-btn-more" data-stack-id="all">All yoinks (${source.length})</button>
        </div>`;
    }
    const rows = filteredYoinks();
    // Map→grid continuity (the category page's Zoltar-zoom fix, ported
    // per the 2026-07-12 journal report): a zoomed map viewport keeps
    // filtering the cards after toggling back. When everything fits the
    // viewport there's nothing to filter.
    const areaRows = mapArea ? rowsInArea(rows, mapArea) : rows;
    const areaActive = mapArea && areaRows.length < rows.length;
    const list = areaActive ? areaRows : rows;
    // The masthead, the owner's story and Challenge all moved into the
    // focused hero (2026-07-27) — the body is just the cards now. Challenge
    // stays category-scoped and still links to the CATEGORY page where the
    // leaderboard lives (user follow-up 2026-07-19: not the filtered
    // profile); the hero renders it.
    const stack = focusStack();
    if (stack) ensureStory(stack.id);
    return `
      ${areaActive ? `
      <p class="log-hint feed-area-line">Showing ${list.length} of ${rows.length} in the map’s view ·
        <button type="button" class="v2-search-clear" data-clear-map-area>Show everywhere</button>
      </p>` : ''}
      <div class="v2-grid">${list.slice(0, visibleCount).map(yoinkCard).join('')}</div>
      <div class="v2-feed-foot" aria-live="polite">
        ${list.length > visibleCount ? '<button type="button" class="v2-btn-more" data-show-more>Show more</button>' : ''}
      </div>`;
  }

  /**
   * The stacks grid alone — patched in place while typing in the filter,
   * which is why the private-journal tile is built here rather than
   * outside: a patch that dropped it would make it vanish mid-search.
   * The filter treats it like any other stack — it matches "journal" or
   * "private" and steps aside for anything else.
   */
  function stacksGridHtml(stacks) {
    const needle = stackTerm.trim().toLowerCase();
    const shown = needle
      ? stacks.filter((s) => s.name.toLowerCase().includes(needle))
      : stacks;
    return (journalTilesHtml(needle) + shown.map(stackCard).join('')) ||
      '<p class="log-hint">No category by that name.</p>';
  }

  /**
   * The ?category= value for the slice on screen ('' on the cover).
   * Two callers: the shareable deep link, and the embed snippet — which
   * needs it because this page's identity is the query string, not the
   * path (src/ui/embed.js embedPageUrl).
   */
  function filterSlug() {
    const stacks = allYoinks ? deriveCategoryStacks(allYoinks) : [];
    const slug = catFilter === 'all'
      ? 'all'
      : catFilter === UNFILED_STACK
        ? 'uncategorized' // only ever resolves on the owner's own view
        : slugify(stacks.find((c) => c.id === catFilter)?.name || '');
    return catFilter && slug ? slug : '';
  }

  /** Keep the ?category= deep link shareable, like V1 (replaceState). */
  function syncFilterUrl() {
    setUrlParam('category', filterSlug());
  }

  async function onStorySave() {
    const input = el.querySelector('[data-story-input]');
    const categoryId = catFilter;
    if (!input || storySaving || !categoryId || categoryId === 'all') return;
    storySaving = true;
    storyError = '';
    storyDraft = input.value;
    patchStoryBox(); // "Saving…" — the draft carries the text through the repaint
    try {
      storyByCat[categoryId] = await saveCollectionStory(profile.id, categoryId, storyDraft);
      storyEditing = false;
      storyDraft = null;
    } catch {
      storyError = 'Couldn’t save the story. Please try again.';
    }
    storySaving = false;
    if (!destroyed) patchStoryBox();
  }

  async function onFollowToggle() {
    if (followBusy || !profile) return;
    followBusy = true;
    followError = '';
    render();
    try {
      if (isFollowing(store, profile.id)) {
        await unfollow(store, profile.id);
      } else {
        await follow(store, profile.id);
      }
      // store update re-renders via the subscription
    } catch {
      followError = 'That didn’t save — please try again.';
    }
    followBusy = false;
    if (!destroyed) render();
  }

  // The explorer's journal as a card set: the TOP CATEGORIES, exactly
  // like the journal's stacks landing (user ask 2026-07-15) — cover is
  // the #1 stack's photo under their name, then one card per stack
  // captioned "In-N-Out · 382 places". All from the already-loaded
  // journal, no extra queries.
  const sharePhotos = createSharePhotosHandler(el, () => {
    if (!profile || !Array.isArray(allYoinks)) return null;
    const name = profile.full_name || profile.username;
    const stacks = deriveCategoryStacks(allYoinks, trophies)
      .filter((s) => s.heroUrl).slice(0, 10);
    // thumbUrl, not the originals: the grid just displayed these exact
    // URLs, so they come from cache and the cards build inside the
    // tap's activation window — cold full-size downloads blew past it
    // and Safari refused the file share (on-device, 2026-07-15)
    const photos = stacks.length
      ? [thumbUrl(stacks[0].heroUrl), ...stacks.map((s) => ({
          src: thumbUrl(s.heroUrl),
          caption: `${s.name} · ${s.places} place${s.places === 1 ? '' : 's'}`,
        }))]
      : [];
    return {
      baseName: `yoink-${slugify(name) || 'journal'}`,
      title: name,
      metaLine: `@${profile.username}  ·  ${allYoinks.length} yoink${allYoinks.length === 1 ? '' : 's'}`,
      byline: '',
      stampDate: null,
      category: null,
      photos,
      hashtags: hashtags({}),
    };
  });

  function onClick(event) {
    if (handleShareClick(event)) return;
    if (handleEmbedClick(event)) return;
    if (sharePhotos(event)) return;
    if (event.target.closest('[data-edit-profile]')) {
      router.navigate('account');
      return;
    }
    if (event.target.closest('[data-import-places]')) {
      router.navigate('import');
      return;
    }
    const door = event.target.closest('[data-door]');
    if (door) {
      const route = door.dataset.door;
      if (route === 'lists') router.navigate('lists'); // own journal → My lists
      else router.navigate(route, { username: profile.username });
      return;
    }
    // The two stamps that stay on this page: Yoinks opens the flat "all
    // yoinks" grid, Locations opens the same journal on the map. Both land
    // at the top of the focused page (scrollToPageTop) rather than at the
    // cards: since the focus treatment landed, the masthead above them IS
    // the answer — it names the slice you opened and shows its collage.
    const jump = event.target.closest('[data-jump]');
    if (jump) {
      // Both land on the flat all-yoinks slice — one as cards, one as pins
      // — so both get the focused chrome and its way back (2026-07-27:
      // "on click of any of those circles the focus should shift forward
      // and then offer a way back to the cover page")
      catFilter = 'all';
      visibleCount = VISIBLE_STEP;
      syncFilterUrl();
      viewMode = jump.dataset.jump === 'map' ? 'map' : 'grid';
      setUrlParam('view', viewMode === 'map' ? 'map' : '');
      store.set({ viewMode });
      render();
      scrollToPageTop();
      return;
    }
    const view = event.target.closest('[data-profile-view]');
    if (view) {
      if (view.dataset.profileView !== viewMode) {
        viewMode = view.dataset.profileView;
        setUrlParam('view', viewMode === 'map' ? 'map' : '');
        store.set({ viewMode }); // the mode follows you to other pages (V1)
        render();
      }
      return;
    }
    if (event.target.closest('[data-follow-action]')) {
      onFollowToggle();
      return;
    }
    if (event.target.closest('[data-clear-map-area]')) {
      mapArea = null;
      render();
      return;
    }
    if (event.target.closest('[data-clear-region]')) {
      regionFilter = null;
      regionRows = null;
      setUrlParam('region', '');
      mapFitDone = false; // an open map reframes to the whole journal (V1 clearRegionFilter)
      render();
      return;
    }
    const shelfTileBtn = event.target.closest('[data-shelf-tile]');
    if (shelfTileBtn) {
      router.navigate('personal-category', {
        username: profile.username, slug: shelfTileBtn.dataset.shelfTile,
      });
      return;
    }
    const stackBtn = event.target.closest('[data-stack-id]');
    if (stackBtn) {
      catFilter = stackBtn.dataset.stackId;
      visibleCount = VISIBLE_STEP;
      storyEditing = false; // an open editor never follows to another category
      storyError = '';
      storyDraft = null;
      syncFilterUrl();
      render();
      scrollToPageTop();
      return;
    }
    if (event.target.closest('[data-stacks-back]')) {
      catFilter = null;
      storyEditing = false;
      storyError = '';
      storyDraft = null;
      syncFilterUrl();
      render();
      scrollToPageTop();
      return;
    }
    if (event.target.closest('[data-story-edit]')) {
      storyEditing = true;
      storyError = '';
      storyDraft = null; // start from the saved story
      patchStoryBox();
      el.querySelector('[data-story-input]')?.focus();
      return;
    }
    if (event.target.closest('[data-story-cancel]')) {
      storyEditing = false;
      storyError = '';
      storyDraft = null;
      patchStoryBox();
      return;
    }
    if (event.target.closest('[data-story-save]')) {
      onStorySave();
      return;
    }
    if (event.target.closest('[data-show-more]')) {
      visibleCount += VISIBLE_STEP;
      render();
      return;
    }
    const challengeBtn = event.target.closest('[data-profile-challenge]');
    if (challengeBtn && allYoinks) {
      const stack = deriveCategoryStacks(allYoinks).find((c) => c.id === catFilter);
      if (stack) {
        // Links to the CATEGORY page (its leaderboard), like the category
        // page's own Challenge button — built synchronously so the share
        // fires inside the tap's activation window (no await before it)
        shareTextFrom(challengeBtn, {
          title: `${stack.name} on Yoink Adventures`,
          text: challengeText(stack.name, { locationCount: stack.places, rank: null }),
          url: `${window.location.origin}${basePath}/category/${slugify(stack.name)}`,
        });
      }
      return;
    }
    const personalRow = event.target.closest('[data-personal-slug]');
    if (personalRow) {
      router.navigate('personal-category', {
        username: personalRow.dataset.personalOwner, slug: personalRow.dataset.personalSlug,
      });
      return;
    }
    const catRow = event.target.closest('[data-category-slug]');
    if (catRow) {
      router.navigate('category', { slug: catRow.dataset.categorySlug });
      return;
    }
    const userRow = event.target.closest('[data-username]');
    if (userRow && userRow.dataset.username) {
      router.navigate('user', { username: userRow.dataset.username });
      return;
    }
    const locationRow = event.target.closest('[data-location-id]');
    if (locationRow) {
      router.navigate('location', { id: locationRow.dataset.locationId });
      return;
    }
    const card = event.target.closest('[data-yoink-id]');
    if (card) {
      router.navigate('yoink', { id: card.dataset.yoinkId });
    }
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target.closest('[data-yoink-id], [data-location-id], [data-username], [data-category-slug], [data-personal-slug]');
    if (!target || target.tagName === 'BUTTON') return;
    event.preventDefault();
    onClick(event);
  }

  function onInput(event) {
    const storyInput = event.target.closest('[data-story-input]');
    if (storyInput) {
      // Buffer only — a store settle re-rendering mid-sentence must not
      // reset the textarea to the saved story
      storyDraft = storyInput.value;
      return;
    }
    const input = event.target.closest('[data-stack-filter]');
    if (!input) return;
    stackTerm = input.value;
    // Patch only the grid so the input keeps focus mid-word
    const grid = el.querySelector('[data-stacks-grid]');
    if (grid && allYoinks) grid.innerHTML = stacksGridHtml(deriveCategoryStacks(stacksSource(), trophies));
  }

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';

  fetchProfile(params.username)
    .then((found) => {
      if (destroyed) return;
      profile = found;
      if (!profile) {
        render();
        return;
      }
      document.title = `${profile.full_name || profile.username} | Yoink Adventures v2`;
      fetchStartedSets(profile.id).then((sets) => {
        if (destroyed) return;
        startedSets = sets;
        render();
      }).catch(() => {});
      fetchPublicLists(profile.id).then((lists) => {
        if (destroyed) return;
        publicLists = lists;
        render();
      }).catch(() => {}); // schema not applied yet — the strip just stays away
      fetchTrophies(profile.id).then((rows) => {
        if (destroyed) return;
        trophies = rows;
        render();
      }).catch(() => {}); // RPC missing → no trophy case, like V1's fallback
      fetchMilestones(profile.id).then((rows) => {
        if (destroyed) return;
        milestones = rows;
        render();
      }).catch(() => {}); // SQL not applied yet → server-side awards stay away
      fetchEarnedAwardRows(profile.id).then((rows) => {
        if (destroyed) return;
        earnedRows = rows;
        render();
      }).catch(() => {}); // table not applied yet → computed-on-read only
      if (store.get().user?.id === profile.id) {
        fetchBalance(profile.id).then((balance) => {
          if (destroyed || balance === null) return;
          bravoBalance = balance;
          render();
        }).catch(() => {}); // bravos migration not applied → the line stays away
      }
      // The personal-category tiles, every viewer (§11) — resolves []
      // when the schema isn't applied, so the tiles simply stay away
      fetchPersonalCategoriesByOwner(profile.id).then((shelves) => {
        if (destroyed || !shelves.length) return;
        myShelves = shelves;
        if (viewMode !== 'map') render();
      });
      render(); // header paints immediately; the journal fills in below
      return fetchAllUserYoinks(profile.id).then((rows) => {
        if (destroyed) return;
        allYoinks = rows;
        // Your own visit quietly records any tiers not yet in
        // user_awards — the backfill for history that predates the
        // celebration layer. No toasts here; those belong to the save
        // moment in the log flow.
        if (store.get().user?.id === profile.id) {
          syncAwards(store, profile.id).catch(() => {});
        }
        // ?category= deep link (V1 user-panel.js:85-92): resolve once
        // the journal is here — 'all' or a slugified category name
        const wanted = new URLSearchParams(window.location.search).get('category');
        if (wanted === 'all') {
          catFilter = 'all';
        } else if (wanted === 'uncategorized' && store.get().user?.id === profile.id) {
          catFilter = UNFILED_STACK; // the owner's leftovers deep link (§11)
        } else if (wanted) {
          const match = deriveCategoryStacks(rows).find((c) => slugify(c.name) === wanted);
          if (match) catFilter = match.id;
        }
        render();
      });
    })
    .catch(() => {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    });

  // Follow state lives in the store (user, followingIds) — re-render the
  // header when it changes (login mid-view, follows finishing their load).
  const unsubscribe = store.subscribe(() => {
    if (destroyed || !profile) return;
    // In map mode the map owns the screen: re-rendering on store noise
    // (including the map's own center/zoom writes) tears it down and
    // remounts it — the blinking loop from the 2026-07-07 device report
    if (viewMode === 'map') return;
    render();
  });

  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKeydown);
  el.addEventListener('input', onInput);

  return () => {
    destroyed = true;
    mapHandle?.cleanup();
    unsubscribe();
    el.removeEventListener('click', onClick);
    el.removeEventListener('keydown', onKeydown);
    el.removeEventListener('input', onInput);
  };
}
